import { WORKSPACE_DOCUMENT_LIMITS } from './document.mjs';

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml']);
const failure = (code, message, cause) => Object.assign(new Error(message, cause ? { cause } : undefined), { code });
const invalid = message => failure('INVALID_PENPOT_TEMPLATE_SEED', message);
const samePage = (left, right) => left && right && ['origin', 'teamId', 'fileId', 'pageId'].every(key => left[key] === right[key]);

function base64(bytes) {
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 8192) binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  return btoa(binary);
}

/** Keep native scene data intact; only resolve the catalog's local asset URLs. */
export async function preparePenpotTemplateSeed(scene, {
  baseUrl = globalThis.location?.href, fetchImpl = globalThis.fetch,
} = {}) {
  if (!scene || scene.version !== 1 || typeof scene.title !== 'string' || !Array.isArray(scene.elements)
      || !scene.elements.length || scene.elements.length > 2000 || !Array.isArray(scene.assets)) throw invalid('템플릿의 Penpot 장면 형식이 올바르지 않습니다.');
  const result = structuredClone(scene);
  const frames = result.elements.filter(element => element?.type === 'frame');
  if (frames.length !== 1 || result.elements[0] !== frames[0]) throw invalid('템플릿 쪽에는 맨 앞에 원본 보드 하나가 필요합니다.');
  const ids = new Set();
  for (const element of result.elements) {
    if (!element || typeof element.id !== 'string' || !element.id || ids.has(element.id)) throw invalid('템플릿 레이어 ID가 비어 있거나 중복되었습니다.');
    ids.add(element.id);
    if (element !== frames[0] && element.parentId !== frames[0].id) throw invalid('모든 템플릿 레이어는 해당 쪽의 보드에 속해야 합니다.');
  }
  const assetIds = new Set();
  for (const asset of result.assets) {
    if (!asset || typeof asset.id !== 'string' || !asset.id || assetIds.has(asset.id) || !IMAGE_TYPES.has(asset.mime)
        || (typeof asset.href === 'string') === (typeof asset.data === 'string')) throw invalid('템플릿 이미지에는 유일한 ID와 원본 주소 또는 이미지 데이터가 필요합니다.');
    assetIds.add(asset.id);
  }
  for (const element of result.elements) {
    if (element.type === 'image' && !assetIds.has(element.assetId)) throw invalid(`템플릿의 이미지 원본이 없습니다: ${element.assetId}`);
  }
  let total = 0;
  for (const asset of result.assets) {
    let size;
    if (typeof asset.href === 'string') {
      let url, base;
      try { base = new URL(baseUrl); url = new URL(asset.href, base); }
      catch (cause) { throw failure('INVALID_PENPOT_TEMPLATE_ASSET', '템플릿 이미지 주소를 확인해 주세요.', cause); }
      if (!['http:', 'https:'].includes(url.protocol) || url.origin !== base.origin || url.username || url.password) throw invalid('같은 콘텐츠 서버의 템플릿 이미지만 사용할 수 있습니다.');
      const response = await fetchImpl(url.href, { credentials: 'same-origin', redirect: 'error', signal: AbortSignal.timeout(30000) });
      if (!response.ok) throw failure('PENPOT_TEMPLATE_ASSET_UNAVAILABLE', `템플릿 이미지를 읽지 못했습니다 (${response.status}).`);
      if (Number(response.headers.get('content-length')) > WORKSPACE_DOCUMENT_LIMITS.assetBytes) throw invalid('템플릿 이미지 하나는 4 MiB 이하여야 합니다.');
      const blob = await response.blob();
      if (blob.type.split(';', 1)[0] !== asset.mime) throw invalid('템플릿 이미지의 실제 형식이 등록된 형식과 다릅니다.');
      size = blob.size;
      if (size > WORKSPACE_DOCUMENT_LIMITS.assetBytes) throw invalid('템플릿 이미지 하나는 4 MiB 이하여야 합니다.');
      asset.data = base64(new Uint8Array(await blob.arrayBuffer()));
      delete asset.href;
    } else {
      try { size = atob(asset.data).length; }
      catch (cause) { throw failure('INVALID_PENPOT_TEMPLATE_ASSET', '템플릿 이미지 데이터를 읽을 수 없습니다.', cause); }
    }
    total += size;
    if (!size || size > WORKSPACE_DOCUMENT_LIMITS.assetBytes || total > WORKSPACE_DOCUMENT_LIMITS.sectionAssetBytes) throw invalid('템플릿 이미지는 개당 4 MiB, 한 쪽 합계 8 MiB 이하여야 합니다.');
  }
  return result;
}

/** A resolved native mount can still report a disconnected/error state. */
export async function assertPenpotTemplateReady(editor) {
  if (!editor) throw failure('PENPOT_NOT_READY', 'Penpot 원본 편집기가 준비되지 않았습니다.');
  await editor.ready;
  const status = editor.getStatus();
  if (!status.connected) {
    if (status.error) throw failure(status.error.code, status.error.message);
    throw failure('PENPOT_NOT_READY', 'Penpot 원본 편집기가 준비되지 않았습니다.');
  }
  return status;
}

/** Persist the native source and its real board export before committing import. */
export async function materializePenpotTemplateSeed({ editor, scene, persist, assertCurrent = () => {}, ...assetOptions }) {
  if (!editor || typeof persist !== 'function') throw invalid('Penpot 편집기와 원본 저장 함수가 필요합니다.');
  const initialStatus = await assertPenpotTemplateReady(editor);
  const source = editor.getSource();
  const revision = initialStatus.revision;
  const prepared = await preparePenpotTemplateSeed(scene, assetOptions);
  assertCurrent();
  if (!samePage(source, editor.getSource()) || editor.getStatus().revision !== revision) throw failure('PENPOT_TEMPLATE_CHANGED', '템플릿을 준비하는 동안 원본이 변경되었습니다. 다시 열어 주세요.');
  let transactionId = null;
  let persisted = false;
  try {
    const imported = await editor.execute('import_scene', { scene: prepared });
    transactionId = imported?.transactionId;
    if (typeof transactionId !== 'string' || !transactionId || !samePage(source, imported.source) || typeof imported.source.boardId !== 'string') throw failure('INVALID_PENPOT_TEMPLATE_IMPORT', 'Penpot이 올바른 원본 보드와 복구 기록을 반환하지 않았습니다.');
    let exported;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      assertCurrent();
      await editor.flush();
      try {
        exported = await editor.execute('export_board', { boardId: imported.source.boardId });
        break;
      } catch (error) {
        // Native text measurement can finish after the import was persisted.
        // Keep its export consistency guard and retry only that read operation.
        if (error.code !== 'PENPOT_CHANGED_DURING_EXPORT' || attempt === 2) throw error;
      }
    }
    if (!samePage(imported.source, exported?.source) || imported.source.boardId !== exported.source.boardId || !exported.preview) throw failure('INVALID_PENPOT_TEMPLATE_EXPORT', 'Penpot이 해당 원본 보드의 미리보기를 반환하지 않았습니다.');
    assertCurrent();
    const patch = { source: structuredClone(exported.source), preview: structuredClone(exported.preview) };
    await persist(structuredClone(patch));
    persisted = true;
    await editor.execute('commit_scene', { transactionId });
    return patch;
  } catch (cause) {
    // Once the host saved this source, reverting native data would leave its
    // saved preview pointing at a different design. Keep that durable source.
    if (persisted) throw failure('PENPOT_TEMPLATE_COMMIT_FAILED', '템플릿 원본과 미리보기는 저장됐지만 초기화 완료를 확인하지 못했습니다. 저장된 원본을 다시 열어 주세요.', cause);
    if (transactionId) {
      try { await editor.execute('rollback_scene', { transactionId }); }
      catch (rollbackError) { throw failure('PENPOT_SCENE_ROLLBACK_FAILED', `템플릿을 준비하지 못했고 원본 복구도 확인하지 못했습니다. 현재 Penpot 원본을 확인해 주세요. ${rollbackError.message}`, cause); }
    }
    throw cause;
  }
}
